import React from 'react'
import { match } from 'ts-pattern'
import type { primitives } from '@adc/blueprint'
import type { FeatureFlags } from '@adc/ui-adc'
import classnames from 'classnames/bind'
import { useListingContext } from 'common/context/listing'
import * as AuctionDuration from 'common/modules/bidBanner/auctionDuration'
import FAQSection from 'common/modules/bidBanner/faqSection'
import {
  CountdownDuring,
  getEndTimeMoment,
  getServerTimeMoment,
} from 'common/modules/auctionCountdown'
import * as Header from 'common/modules/tpsOnlineBidBanner/header'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import BidForm from 'common/modules/bidBanner/bidForm'
import BidFormUI from 'common/modules/bidBanner/bidForm/ui'
import * as graph from '@adc/sdk'

// TODO: Make this typescript

const cx = classnames.bind({})

const renderRightSide = (props: any) => {
  const { segment } = useListingContext()

  return (
    <>
      <div className={cx('u-mb-6')}>
        <CountdownDuring
          endTime={getEndTimeMoment(segment)}
          currentServerTime={getServerTimeMoment(segment)}
          segment={segment}
        />
      </div>
      <BidForm {...props}>
        {(bidFormContainerProps: any) => (
          <BidFormUI {...bidFormContainerProps} />
        )}
      </BidForm>
    </>
  )
}

const NoBids = (props: any) => {
  const { segment } = useListingContext()

  const { nextBidAmount } = segment.__methods__.calculateAuctionValues()
  return (
    <Container
      ribbonColor='adcLightBlue'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment?.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.NoBids nextBidAmount={nextBidAmount} />
          </div>
          <LeftMarginSection>
            <BidDetails.During />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const BidPlacedPending = (props: any) => {
  const { segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedPending />
          </div>
          <LeftMarginSection>
            <BidDetails.During />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const HighestBidReserveNotMet = (props: any) => {
  const { segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedNotMet />
          </div>
          <LeftMarginSection>
            <BidDetails.During />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const HighestBidReserveMet = (props: any) => {
  const { segment } = useListingContext()
  return (
    <Container
      ribbonColor='success'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.BidPlacedMet />
          </div>
          <LeftMarginSection>
            <BidDetails.During />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

const Outbid = (props: any) => {
  const { segment } = useListingContext()
  return (
    <Container
      ribbonColor='caution'
      left={
        <>
          <div className={cx('u-mb-3')}>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </div>
          <div className={cx('u-mb-3')}>
            <Header.Outbid />
          </div>
          <LeftMarginSection>
            <BidDetails.During />
          </LeftMarginSection>
          <FAQSection />
        </>
      }
      right={renderRightSide(props)}
    />
  )
}

interface DuringProps {
  featureFlags: FeatureFlags
  user: primitives.AuctionUser
}

const During = (props: DuringProps) => {
  const { segment } = useListingContext()
  const status = {
    current_user_bidding_status: segment?.current_user_bidding_status,
    reserve_status: segment?.reserve_status,
  }
  return match(status)
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.NoBid,
      },
      () => <NoBids {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveNotSet,
      },
      () => <BidPlacedPending {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveMet,
      },
      () => <HighestBidReserveMet {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.HighestBid,
        reserve_status: graph.enums.ReserveStatus.ReserveNotMet,
      },
      () => <HighestBidReserveNotMet {...props} />
    )
    .with(
      {
        current_user_bidding_status:
          graph.enums.OnlineAuctionSegmentBiddingStatus.Outbid,
      },
      () => <Outbid {...props} />
    )
    .otherwise(() => (
      <Container
        left={
          <>
            <div>During Auction</div>
            <AuctionDuration.Compact
              startDateTime={segment.start_date}
              endDateTime={segment.initial_end_date}
            />
          </>
        }
        right={renderRightSide(props)}
      />
    ))
}

export default During
